import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { buildApiUrl } from '@/lib/config';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, ArrowUpDown } from "lucide-react";
import { format } from "date-fns";

interface CurrencyOption {
  code: string;
  name: string;
}

const currencies: CurrencyOption[] = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "CHF", name: "Swiss Franc" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "THB", name: "Thai Baht" },
  { code: "AED", name: "UAE Dirham" },
  { code: "HKD", name: "Hong Kong Dollar" },
  { code: "IDR", name: "Indonesian Rupiah" },
  { code: "MYR", name: "Malaysian Ringgit" },
  { code: "NZD", name: "New Zealand Dollar" },
  { code: "SAR", name: "Saudi Arabian Riyal" },
  { code: "VND", name: "Vietnamese Dong" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "INR", name: "Indian Rupee" },
];

export default function CurrencyConverter() {
  const [fromCurrency, setFromCurrency] = useState("USD");
  const [toCurrency, setToCurrency] = useState("INR");
  const [fromAmount, setFromAmount] = useState("1");
  const [toAmount, setToAmount] = useState("0.00");
  const [exchangeRate, setExchangeRate] = useState("0.0000");
  const [lastUpdated, setLastUpdated] = useState("");

  const { data: rates, refetch } = useQuery({
    queryKey: ["/api/forex-rates/today"],
    queryFn: async () => {
      const response = await fetch(buildApiUrl('api/forex-rates/today'));
      if (!response.ok) throw new Error("Failed to fetch rates");
      return response.json();
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const convertCurrency = () => {
    if (!rates || !fromAmount) {
      setToAmount("0.00");
      setExchangeRate("0.0000");
      return;
    }

    const amount = parseFloat(fromAmount);
    if (isNaN(amount)) {
      setToAmount("0.00");
      return;
    }

    // All rates are expressed in terms of INR (how many INR per 1 unit of foreign currency)
    // INR rate should be 1.0000
    let rate = 1;

    if (fromCurrency === "INR" && toCurrency === "INR") {
      rate = 1;
    } else if (fromCurrency === "INR") {
      // Converting from INR to foreign currency
      const toRate = rates.find(r => (r.currencyCode || r.currency_code) === toCurrency)?.rate;
      if (toRate) {
        const numericRate = typeof toRate === 'string' ? parseFloat(toRate) : toRate;
        rate = 1 / numericRate; // INR to foreign currency
      }
    } else if (toCurrency === "INR") {
      // Converting from foreign currency to INR
      const fromRate = rates.find(r => (r.currencyCode || r.currency_code) === fromCurrency)?.rate;
      if (fromRate) {
        rate = typeof fromRate === 'string' ? parseFloat(fromRate) : fromRate; // Foreign currency to INR
      }
    } else {
      // Converting between two foreign currencies through INR
      const fromRate = rates.find(r => (r.currencyCode || r.currency_code) === fromCurrency)?.rate;
      const toRate = rates.find(r => (r.currencyCode || r.currency_code) === toCurrency)?.rate;
      
      if (fromRate && toRate) {
        const numericFromRate = typeof fromRate === 'string' ? parseFloat(fromRate) : fromRate;
        const numericToRate = typeof toRate === 'string' ? parseFloat(toRate) : toRate;
        // Convert from foreign currency to INR, then INR to target foreign currency
        rate = numericFromRate / numericToRate;
      }
    }

    const convertedAmount = amount * rate;
    setToAmount(convertedAmount.toFixed(2));
    setExchangeRate(rate.toFixed(4));
  };

  const swapCurrencies = () => {
    const tempCurrency = fromCurrency;
    const tempAmount = fromAmount;
    
    setFromCurrency(toCurrency);
    setToCurrency(tempCurrency);
    setFromAmount(toAmount);
    setToAmount(tempAmount);
  };

  const handleRefresh = async () => {
    await refetch();
    convertCurrency();
  };

  useEffect(() => {
    convertCurrency();
    setLastUpdated(format(new Date(), "HH:mm:ss"));
  }, [fromCurrency, toCurrency, fromAmount, rates]);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="text-center">Currency Converter</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* From Currency */}
          <div className="space-y-2">
            <Label htmlFor="from-currency">FROM</Label>
            <Select value={fromCurrency} onValueChange={setFromCurrency}>
              <SelectTrigger id="from-currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              value={fromAmount}
              onChange={(e) => setFromAmount(e.target.value)}
              placeholder="Enter amount"
              className="text-lg"
            />
          </div>

          {/* To Currency */}
          <div className="space-y-2">
            <Label htmlFor="to-currency">TO</Label>
            <Select value={toCurrency} onValueChange={setToCurrency}>
              <SelectTrigger id="to-currency">
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((currency) => (
                  <SelectItem key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="bg-purple-50 border border-purple-200 rounded-md p-3">
              <div className="text-2xl font-bold text-purple-700">{toAmount}</div>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center">
          <Button
            variant="outline"
            size="icon"
            onClick={swapCurrencies}
            className="rounded-full bg-orange-500 hover:bg-orange-600 text-white border-orange-500 hover:border-orange-600"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        </div>

        {/* Exchange Rate Info */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">
              Exchange Rate: 1 {fromCurrency} = {exchangeRate} {toCurrency}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="text-orange-600 hover:text-orange-700 border-orange-200 hover:border-orange-300"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          </div>
          <div className="text-xs text-gray-400">
            Last updated: {lastUpdated}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}